import type { QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

/**
 * L'identité sous laquelle la régie lit et écrit : le compte connecté et la
 * galerie ouverte (audit MVP du 23/09, § 2, P1 « session précédente
 * conservée après reconnexion »).
 *
 * Elle n'est posée QUE par les chargeurs de route, depuis une session
 * fraîchement relue (`chargerSession`), et retirée à la déconnexion. Toute
 * transition — autre compte, autre galerie, déconnexion — annule les
 * requêtes en vol et purge les caches métier AVANT le routage : aucune donnée
 * d'une session ne peut être servie sous une autre, même par erreur.
 */
export interface Identite {
  readonly compteId: string;
  /** `null` tant qu'aucune galerie n'est ouverte (racine, connexion). */
  readonly galerieSlug: string | null;
}

/** Premier segment de toute clé de requête métier (`api/cles.ts`). */
export const PREFIXE_METIER = 'metier';

/** Segment « compte » d'une clé posée hors session (tests, écrans publics). */
export const COMPTE_ANONYME = 'anonyme';

let courante: Identite | null = null;
const abonnes = new Set<() => void>();

/**
 * ÉPOQUE de session (relecture du 23/09, constats 1 et 10) : incrémentée à
 * CHAQUE changement d'identité — autre compte, autre galerie, déconnexion.
 * Une requête, une mutation ou un rejeu capture l'époque à son lancement ;
 * devenu obsolète, il ne touche plus à rien. Le contrôleur d'époque annule
 * au niveau HTTP tout ce qui était en vol (`api/client.ts`).
 */
let epoque = 0;
let controleurEpoque = new AbortController();

/**
 * Le serveur a répondu `409 compte_change` : la session n'est plus le compte
 * sous lequel la requête a été lancée (troisième relecture du 23/09, constat
 * N3). Les abonnés purgent les requêtes de ce compte et revalident la session.
 */
const abonnesCompteChange = new Set<(compteId: string) => void>();

export function surCompteChange(rappel: (compteId: string) => void): () => void {
  abonnesCompteChange.add(rappel);
  return () => abonnesCompteChange.delete(rappel);
}

export function annoncerCompteChange(compteId: string): void {
  for (const rappel of abonnesCompteChange) rappel(compteId);
}

/** Levée par ce qui a été lancé sous une identité qui n'est plus la courante. */
export class PorteeObsolete extends Error {
  constructor() {
    super('La session a changé : cette opération a été abandonnée.');
    this.name = 'PorteeObsolete';
  }
}

export function estPorteeObsolete(erreur: unknown): erreur is PorteeObsolete {
  return erreur instanceof PorteeObsolete;
}

function changerEpoque(): void {
  epoque += 1;
  const precedent = controleurEpoque;
  controleurEpoque = new AbortController();
  precedent.abort(new PorteeObsolete());
}

export function epoqueCourante(): number {
  return epoque;
}

/** Signal annulé au prochain changement d'identité (transmis à chaque `fetch`). */
export function signalDEpoque(): AbortSignal {
  return controleurEpoque.signal;
}

/** Ce qu'une opération capture à son lancement. */
export interface CapturePortee {
  readonly compteId: string | null;
  readonly galerieSlug: string | null;
  readonly epoque: number;
  /** Le signal de CETTE époque : annulé au prochain changement d'identité. */
  readonly signal: AbortSignal;
}

export function capturerPortee(): CapturePortee {
  return {
    compteId: courante?.compteId ?? null,
    galerieSlug: courante?.galerieSlug ?? null,
    epoque,
    signal: controleurEpoque.signal,
  };
}

export function estActuelle(capture: CapturePortee | undefined): capture is CapturePortee {
  return capture !== undefined && capture.epoque === epoque;
}

/** Lève `PorteeObsolete` si l'identité a changé depuis `capture`. */
export function exigerActuelle(
  capture: CapturePortee | undefined,
): asserts capture is CapturePortee {
  if (!estActuelle(capture)) throw new PorteeObsolete();
}

function annoncer(): void {
  for (const abonne of abonnes) abonne();
}

export function identiteCourante(): Identite | null {
  return courante;
}

function abonner(abonne: () => void): () => void {
  abonnes.add(abonne);
  return () => abonnes.delete(abonne);
}

/** L'identité courante, réactive (le composant se redessine si elle change). */
export function useIdentite(): Identite | null {
  return useSyncExternalStore(abonner, identiteCourante, identiteCourante);
}

/**
 * Annule les requêtes métier en vol puis retire leurs entrées du cache. La
 * session (`['session']`) est épargnée : elle vient d'être relue par
 * l'appelant, qui décide lui-même de la garder.
 */
export async function purgerCachesMetier(client: QueryClient): Promise<void> {
  await client.cancelQueries({ queryKey: [PREFIXE_METIER] });
  client.removeQueries({ queryKey: [PREFIXE_METIER] });
}

/**
 * Adopte l'identité d'une session fraîchement relue. Un autre compte, ou
 * une autre galerie, purge d'abord tous les caches métier.
 *
 * `galerieSlug` à `null` (chargeur de la racine) garde la galerie courante
 * si le compte ne change pas : ce n'est pas une transition.
 */
export async function adopterIdentite(
  client: QueryClient,
  compteId: string,
  galerieSlug: string | null,
): Promise<void> {
  const precedente = courante;
  const memeCompte = precedente?.compteId === compteId;
  const galerie = galerieSlug ?? (memeCompte ? (precedente?.galerieSlug ?? null) : null);
  const memeGalerie =
    memeCompte &&
    (galerie === null || precedente?.galerieSlug === null || precedente?.galerieSlug === galerie);

  if (!memeCompte || !memeGalerie) {
    // L'identité et l'époque changent AVANT la purge : une requête relancée
    // pendant l'annulation se clé déjà sous la nouvelle identité, et tout ce
    // qui était en vol est annulé jusqu'au `fetch`.
    courante = { compteId, galerieSlug: galerie };
    changerEpoque();
    await purgerCachesMetier(client);
    if (precedente && !memeCompte) {
      for (const rappel of abonnesChangementCompte) await rappel();
    }
    annoncer();
    return;
  }
  if (precedente?.galerieSlug !== galerie) {
    courante = { compteId, galerieSlug: galerie };
    changerEpoque();
    annoncer();
  }
}

/**
 * Mêmes compte et galerie, mais autorisations CHANGÉES (quatrième relecture,
 * point 3) : une nouvelle époque commence AVANT la purge. Les requêtes en vol
 * sont annulées, et les mutations lancées sous les anciens droits deviennent
 * obsolètes — leurs callbacks (rollback optimiste, `setQueryData`) ne
 * réintroduisent plus rien de ce qui vient d'être purgé.
 */
export function ouvrirEpoqueDeDroits(): void {
  changerEpoque();
}

/**
 * Déconnexion, session expirée : plus aucune identité. Toutes les requêtes
 * en vol sont annulées et le cache ENTIER est vidé, session comprise.
 */
/** Ce qui doit disparaître avec l'identité (M11 : l'instantané hors ligne). */
export interface OptionsOubli {
  readonly localeSeulement?: boolean;
}
const abonnesOubli = new Set<(options: OptionsOubli) => Promise<void> | void>();

export function surOubliIdentite(
  rappel: (options: OptionsOubli) => Promise<void> | void,
): () => void {
  abonnesOubli.add(rappel);
  return () => abonnesOubli.delete(rappel);
}

export async function oublierIdentite(
  client: QueryClient,
  options: OptionsOubli = {},
): Promise<void> {
  courante = null;
  changerEpoque();
  annoncer();
  await client.cancelQueries();
  client.clear();
  for (const rappel of abonnesOubli) await rappel(options);
}

const abonnesChangementCompte = new Set<() => Promise<void>>();

export function surChangementCompte(rappel: () => Promise<void>): () => void {
  abonnesChangementCompte.add(rappel);
  return () => {
    abonnesChangementCompte.delete(rappel);
  };
}
